import { useState, useCallback } from 'react';
import { Room, RemoteParticipant } from 'livekit-client';
import { v4 as uuidv4 } from 'uuid';
import { ChatMessage } from '../types';

interface UseChatProps {
  room: Room | null;
}

export const useChat = ({ room }: UseChatProps) => {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');

  // Send message
  const sendMessage = useCallback(async (message: string): Promise<void> => {
    if (!room || !message.trim()) {
      return;
    }

    try {
      const encoder = new TextEncoder();
      const payload = encoder.encode(JSON.stringify({
        type: 'chat',
        message: message.trim(),
        timestamp: Date.now()
      }));

      await room.localParticipant.publishData(payload, { reliable: true });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [room]);

  // Handle incoming data messages
  const handleDataReceived = useCallback((payload: Uint8Array, participant?: RemoteParticipant) => {
    try {
      const data = JSON.parse(new TextDecoder().decode(payload));
      if (data.type === 'chat') {
        const newChatMessage: ChatMessage = {
          id: uuidv4(),
          participantId: participant?.sid || 'system',
          participantName: participant?.name || participant?.identity || 'System',
          message: data.message,
          timestamp: new Date(data.timestamp || Date.now())
        };

        setChatMessages(prev => [...prev, newChatMessage]);
      }
    } catch (error) {
      console.error('Error parsing data message:', error);
    }
  }, []);

  // Clear messages
  const clearMessages = useCallback(() => {
    setChatMessages([]);
  }, []);

  return {
    chatMessages,
    newMessage,
    setNewMessage,
    sendMessage,
    handleDataReceived,
    clearMessages,
  };
};
